<script setup lang="ts" generic="T">
/**
 * Kanban board: columns of cards, drag & drop between and inside columns.
 *
 * The board owns no data. Columns and cards come from the caller, the card
 * body is the `card` slot, and a finished move is only EMITTED (`move`) — the
 * caller persists it and hands back new columns (`applyBoardMove` does the
 * local update in one line). One component covers a status board, an issue
 * board and a week view with one column per day.
 *
 * Drop guard: `canMove` is asked while a card hovers a column (the column then
 * shows it will not take the card) and once more before `move` fires. A
 * refused move emits `move-rejected` instead, so the caller can say why.
 *
 * Pointer: a click selects a card (`v-model:selected`, `select`), a double
 * click opens it (`open`). Keyboard: every card is a tab stop. Arrow keys walk
 * between cards (up/down) and columns (left/right); Alt + arrow moves the
 * focused card — left/right to the neighbouring column, up/down inside its
 * column. Space selects, Enter opens. Every keyboard move is announced through
 * a polite live region.
 *
 * `readonly` keeps selecting and opening but turns off every move — a week view
 * that only shows what is due.
 *
 * Controls inside a card (a toggle, a link) keep their clicks and keys: they
 * neither select nor open the card, and Space / Enter on them stay theirs. An
 * app region whose clicks stay its own carries `data-nx-ignore-click`.
 *
 * Layout and behaviour options (I-2382, kit request K7, all off by default):
 * `fill` lets the columns share the board's width equally and reach its full
 * height, each scrolling as a whole (head on top, footer after the cards), and
 * the board fill its parent; `reorder=false`
 * makes a move change the column only (a status board without a manual order:
 * no insertion line, no Alt + ↑/↓); a column's `count` replaces the number of
 * cards it holds in the head (a paged column shows its total), and a column
 * shows its empty row only when it is proven empty (total 0, or no total and
 * no cards).
 */
import { computed, nextTick, ref, useId } from 'vue';
import {
  boardAccentColor,
  isNoopMove,
  resolveDropIndex,
  type NxBoardAccent,
  type NxBoardColumn,
  type NxBoardKey,
  type NxBoardMove,
} from './board';

const props = withDefaults(
  defineProps<{
    columns: NxBoardColumn<T>[];
    /** Stable key of a card. Defaults to `item.id`. */
    itemKey?: (item: T) => NxBoardKey;
    /** Accessible name of a card, also used in the announcements. Defaults to the key. */
    itemLabel?: (item: T) => string;
    /** Drop guard. Return `false` to refuse a move. */
    canMove?: (move: NxBoardMove<T>) => boolean;
    /** Accent edge of a card, e.g. its status or priority. */
    cardAccent?: (item: T) => NxBoardAccent | null | undefined;
    /** Cards can be selected and opened, but not moved. */
    readonly?: boolean;
    /** Accessible name of the whole board. */
    ariaLabel?: string;
    /** Width of one column (CSS length). Ignored with `fill`. */
    columnWidth?: string;
    /** Columns share the width equally and reach the board's full height; the board fills its parent. */
    fill?: boolean;
    /** `false`: a move changes the column only — no position inside a column, no insertion line. */
    reorder?: boolean;
    /** Screen-reader text for a column whose `count` is `null`. */
    countUnavailableLabel?: string;
    /** Shown in a column without cards (the `empty` slot overrides it). */
    emptyLabel?: string;
    /** Announcement after a keyboard move. */
    movedLabel?: (item: string, column: string, position: number) => string;
    /** Announcement when the guard refused a move. */
    rejectedLabel?: (item: string, column: string) => string;
  }>(),
  {
    itemKey: (item: T) => (item as { id: NxBoardKey }).id,
    itemLabel: undefined,
    canMove: undefined,
    cardAccent: undefined,
    readonly: false,
    ariaLabel: undefined,
    columnWidth: 'calc(var(--nxt-size-unit) * 17)',
    fill: false,
    reorder: true,
    countUnavailableLabel: 'Anzahl nicht verfügbar',
    emptyLabel: 'Keine Einträge',
    movedLabel: (item: string, column: string, position: number) =>
      `„${item}“ nach „${column}“ verschoben, Position ${position}`,
    rejectedLabel: (item: string, column: string) =>
      `„${item}“ kann nicht nach „${column}“ verschoben werden`,
  }
);

const emit = defineEmits<{
  move: [move: NxBoardMove<T>];
  'move-rejected': [move: NxBoardMove<T>];
  select: [item: T, column: NxBoardColumn<T>];
  open: [item: T, column: NxBoardColumn<T>];
}>();

/** Key of the selected card, `null` for none. */
const selected = defineModel<NxBoardKey | null>('selected', { default: null });

defineSlots<{
  card(props: { item: T; column: NxBoardColumn<T>; index: number }): unknown;
  'column-header'?(props: { column: NxBoardColumn<T>; count: number; overLimit: boolean }): unknown;
  'column-actions'?(props: { column: NxBoardColumn<T> }): unknown;
  'column-footer'?(props: { column: NxBoardColumn<T> }): unknown;
  empty?(props: { column: NxBoardColumn<T> }): unknown;
}>();

const boardId = useId();
const rootRef = ref<HTMLElement | null>(null);
const announcement = ref('');

function labelOf(item: T): string {
  return props.itemLabel ? props.itemLabel(item) : String(props.itemKey(item));
}

/** The column's total: its `count` when set (`null` = unknown), else the cards it holds. */
function totalOf(column: NxBoardColumn<T>): number | null {
  return column.count === undefined ? column.items.length : column.count;
}

/** „3“, or „3/2“ against a limit; „–“ when the total is unknown. */
function countText(column: NxBoardColumn<T>): string {
  const total = totalOf(column);
  if (total === null) return '–';
  return column.limit != null ? `${total}/${column.limit}` : String(total);
}

// Everything a click or key inside a card can mean for a control of its own.
const CONTROLS =
  'a[href], button, input, select, textarea, summary, label, [contenteditable]:not([contenteditable="false"]), [role="button"], [role="link"], [role="checkbox"], [role="switch"], [role="menuitem"], [role="option"], [role="tab"], [data-nx-ignore-click]';

/** True when the event started on a control inside the card, not on the card itself. */
function fromControl(event: Event): boolean {
  const card = event.currentTarget as Element | null;
  const hit = (event.target as Element | null)?.closest?.(CONTROLS);
  return !!hit && hit !== card && !!card?.contains(hit);
}

function onCardClick(event: MouseEvent, item: T, column: NxBoardColumn<T>) {
  if (fromControl(event)) return;
  select(item, column);
}

function onCardDblclick(event: MouseEvent, item: T, column: NxBoardColumn<T>) {
  if (fromControl(event)) return;
  emit('open', item, column);
}

/**
 * Empty only when proven: no cards and no total, or a total of 0. A paged
 * column whose total is unknown (`null`) or larger than nothing is not empty.
 */
function isEmpty(column: NxBoardColumn<T>): boolean {
  return column.items.length === 0 && (column.count === undefined || column.count === 0);
}

function canDrag(column: NxBoardColumn<T>): boolean {
  return !props.readonly && !column.disabled;
}

function select(item: T, column: NxBoardColumn<T>) {
  selected.value = props.itemKey(item);
  emit('select', item, column);
}

function cardStyle(item: T) {
  const color = props.cardAccent ? boardAccentColor(props.cardAccent(item)) : null;
  return color ? { '--board-card-accent': color } : undefined;
}

function columnStyle(column: NxBoardColumn<T>) {
  const color = boardAccentColor(column.tone);
  return color ? { '--board-tone': color } : undefined;
}

function overLimit(column: NxBoardColumn<T>): boolean {
  const total = totalOf(column);
  return column.limit != null && total !== null && total > column.limit;
}

function buildMove(
  item: T,
  from: NxBoardColumn<T>,
  fromIndex: number,
  to: NxBoardColumn<T>,
  index: number
): NxBoardMove<T> {
  return { item, itemKey: props.itemKey(item), from: from.id, to: to.id, fromIndex, index };
}

function allowed(move: NxBoardMove<T>, target: NxBoardColumn<T>): boolean {
  if (target.disabled) return false;
  return props.canMove ? props.canMove(move) !== false : true;
}

/** Emit a move after the guard. Returns whether it went through. */
function commit(move: NxBoardMove<T>, target: NxBoardColumn<T>): boolean {
  if (isNoopMove(move)) return false;
  if (!allowed(move, target)) {
    emit('move-rejected', move);
    announcement.value = props.rejectedLabel(labelOf(move.item), target.title);
    return false;
  }
  emit('move', move);
  return true;
}

// ── Pointer drag & drop ────────────────────────────────────────────────────
type DragState = { item: T; column: NxBoardColumn<T>; fromIndex: number };
const dragging = ref<DragState | null>(null) as { value: DragState | null };
const dropTarget = ref<{ columnId: NxBoardKey; slot: number; ok: boolean } | null>(null);

const draggingKey = computed(() =>
  dragging.value ? props.itemKey(dragging.value.item) : undefined
);

function onCardDragStart(event: DragEvent, item: T, column: NxBoardColumn<T>, index: number) {
  if (!canDrag(column)) {
    event.preventDefault();
    return;
  }
  dragging.value = { item, column, fromIndex: index };
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move';
    // Firefox starts no drag without data; the payload itself is unused.
    event.dataTransfer.setData('text/plain', String(props.itemKey(item)));
  }
}

function onDragEnd() {
  dragging.value = null;
  dropTarget.value = null;
}

function updateTarget(event: DragEvent, column: NxBoardColumn<T>, slot: number) {
  const drag = dragging.value;
  if (!drag) return;
  if (!props.reorder) {
    // Column only: the own column is no target, another one takes the card at its end.
    if (drag.column.id === column.id) {
      dropTarget.value = null;
      return;
    }
    slot = column.items.length;
  }
  const index = resolveDropIndex(drag.column.id === column.id, drag.fromIndex, slot);
  const move = buildMove(drag.item, drag.column, drag.fromIndex, column, index);
  const ok = isNoopMove(move) || allowed(move, column);
  dropTarget.value = { columnId: column.id, slot, ok };
  if (ok) {
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
  }
}

/** Over a card: the gap above or below it, whichever half the pointer is in. */
function onCardDragOver(event: DragEvent, column: NxBoardColumn<T>, index: number) {
  const el = event.currentTarget as HTMLElement;
  const rect = el.getBoundingClientRect();
  const below = event.clientY > rect.top + rect.height / 2;
  updateTarget(event, column, below ? index + 1 : index);
}

/** Over the column but not over a card: the end of the column. */
function onBodyDragOver(event: DragEvent, column: NxBoardColumn<T>) {
  updateTarget(event, column, column.items.length);
}

function onBodyDragLeave(event: DragEvent) {
  const next = event.relatedTarget as Node | null;
  if (next && (event.currentTarget as HTMLElement).contains(next)) return;
  dropTarget.value = null;
}

function onDrop(column: NxBoardColumn<T>) {
  const drag = dragging.value;
  const target = dropTarget.value;
  onDragEnd();
  if (!drag || !target || target.columnId !== column.id) return;
  const index = resolveDropIndex(drag.column.id === column.id, drag.fromIndex, target.slot);
  commit(buildMove(drag.item, drag.column, drag.fromIndex, column, index), column);
}

function dropSide(column: NxBoardColumn<T>, index: number): 'before' | 'after' | null {
  if (!props.reorder) return null;
  const target = dropTarget.value;
  if (!target || !target.ok || target.columnId !== column.id) return null;
  if (target.slot === index) return 'before';
  if (index === column.items.length - 1 && target.slot === column.items.length) return 'after';
  return null;
}

function columnDropState(column: NxBoardColumn<T>): 'ok' | 'rejected' | null {
  const target = dropTarget.value;
  if (!target || target.columnId !== column.id) return null;
  return target.ok ? 'ok' : 'rejected';
}

// ── Keyboard ───────────────────────────────────────────────────────────────
function focusCard(key: NxBoardKey) {
  // Compared by attribute rather than a selector: keys are arbitrary strings.
  const cards = rootRef.value?.querySelectorAll<HTMLElement>('[data-board-card]') ?? [];
  for (const card of cards) {
    if (card.dataset.boardCard === String(key)) {
      card.focus();
      return;
    }
  }
}

function focusAt(columnIndex: number, cardIndex: number) {
  const column = props.columns[columnIndex];
  if (!column || column.items.length === 0) return false;
  const item = column.items[Math.min(cardIndex, column.items.length - 1)];
  focusCard(props.itemKey(item));
  return true;
}

/** Nearest column in `direction` that accepts cards (skips disabled ones). */
function neighbourColumn(columnIndex: number, direction: -1 | 1): number {
  for (let i = columnIndex + direction; i >= 0 && i < props.columns.length; i += direction) {
    if (!props.columns[i].disabled) return i;
  }
  return -1;
}

async function keyboardMove(
  item: T,
  column: NxBoardColumn<T>,
  fromIndex: number,
  target: NxBoardColumn<T>,
  index: number
) {
  const key = props.itemKey(item);
  if (!commit(buildMove(item, column, fromIndex, target, index), target)) return;
  announcement.value = props.movedLabel(labelOf(item), target.title, index + 1);
  // The caller updates `columns` in its move handler; follow the card there.
  await nextTick();
  focusCard(key);
}

function onCardKeydown(
  event: KeyboardEvent,
  item: T,
  column: NxBoardColumn<T>,
  columnIndex: number,
  index: number
) {
  // Keys on a control inside the card are that control's (a toggle's Space / Enter).
  if (event.target !== event.currentTarget) return;
  const { key } = event;
  if (key === 'Enter') {
    event.preventDefault();
    emit('open', item, column);
    return;
  }
  if (key === ' ') {
    event.preventDefault();
    select(item, column);
    return;
  }
  if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(key)) return;
  event.preventDefault();

  if (event.altKey) {
    if (!canDrag(column)) return;
    if (key === 'ArrowUp' && index > 0 && props.reorder) {
      void keyboardMove(item, column, index, column, index - 1);
    } else if (key === 'ArrowDown' && index < column.items.length - 1 && props.reorder) {
      void keyboardMove(item, column, index, column, index + 1);
    } else if (key === 'ArrowLeft' || key === 'ArrowRight') {
      const next = neighbourColumn(columnIndex, key === 'ArrowLeft' ? -1 : 1);
      if (next < 0) return;
      const target = props.columns[next];
      const at = props.reorder ? Math.min(index, target.items.length) : target.items.length;
      void keyboardMove(item, column, index, target, at);
    }
    return;
  }

  if (key === 'ArrowUp') focusAt(columnIndex, Math.max(0, index - 1));
  else if (key === 'ArrowDown') focusAt(columnIndex, index + 1);
  else {
    const step = key === 'ArrowLeft' ? -1 : 1;
    for (let i = columnIndex + step; i >= 0 && i < props.columns.length; i += step) {
      if (focusAt(i, index)) return;
    }
  }
}
</script>

<template>
  <div
    ref="rootRef"
    class="nx-board"
    :class="{ 'is-fill': fill }"
    role="region"
    :aria-label="ariaLabel"
    :style="{ '--board-column-width': columnWidth }"
  >
    <section
      v-for="(column, columnIndex) in columns"
      :key="column.id"
      class="nx-board__column"
      :class="{
        'has-tone': !!columnStyle(column),
        'is-current': column.current,
        'is-disabled': column.disabled,
        'is-drop-ok': columnDropState(column) === 'ok',
        'is-drop-rejected': columnDropState(column) === 'rejected',
      }"
      :style="columnStyle(column)"
      :aria-labelledby="`${boardId}-col-${columnIndex}`"
      :aria-current="column.current ? 'date' : undefined"
    >
      <header class="nx-board__header">
        <slot
          name="column-header"
          :column="column"
          :count="column.items.length"
          :over-limit="overLimit(column)"
        >
          <span class="nx-board__marker" aria-hidden="true" />
          <span :id="`${boardId}-col-${columnIndex}`" class="nx-board__title">
            {{ column.title }}
          </span>
          <span class="nx-board__count" :class="{ 'is-over': overLimit(column) }">
            <template v-if="totalOf(column) === null">
              <span aria-hidden="true">{{ countText(column) }}</span>
              <span class="nx-board__sr">{{ countUnavailableLabel }}</span>
            </template>
            <template v-else>{{ countText(column) }}</template>
          </span>
        </slot>
        <slot name="column-actions" :column="column" />
      </header>

      <ul
        class="nx-board__body"
        :aria-labelledby="`${boardId}-col-${columnIndex}`"
        @dragover="onBodyDragOver($event, column)"
        @dragleave="onBodyDragLeave"
        @drop.prevent="onDrop(column)"
      >
        <li
          v-for="(item, index) in column.items"
          :key="itemKey(item)"
          class="nx-board__card"
          :class="{
            'is-dragging': draggingKey === itemKey(item),
            'is-drop-before': dropSide(column, index) === 'before',
            'is-drop-after': dropSide(column, index) === 'after',
            'is-selected': selected === itemKey(item),
            'is-static': !canDrag(column),
            'has-accent': !!cardStyle(item),
          }"
          :style="cardStyle(item)"
          tabindex="0"
          :draggable="canDrag(column)"
          :aria-label="labelOf(item)"
          :aria-current="selected === itemKey(item) ? 'true' : undefined"
          :data-board-card="itemKey(item)"
          @click="onCardClick($event, item, column)"
          @dblclick="onCardDblclick($event, item, column)"
          @dragstart="onCardDragStart($event, item, column, index)"
          @dragend="onDragEnd"
          @dragover.stop="onCardDragOver($event, column, index)"
          @keydown="onCardKeydown($event, item, column, columnIndex, index)"
        >
          <slot name="card" :item="item" :column="column" :index="index">
            {{ labelOf(item) }}
          </slot>
        </li>
        <li v-if="isEmpty(column)" class="nx-board__empty">
          <slot name="empty" :column="column">{{ emptyLabel }}</slot>
        </li>
      </ul>

      <footer v-if="$slots['column-footer']" class="nx-board__footer">
        <slot name="column-footer" :column="column" />
      </footer>
    </section>

    <div class="nx-board__live" aria-live="polite" role="status">{{ announcement }}</div>
  </div>
</template>

<style scoped>
.nx-board {
  display: flex;
  align-items: flex-start;
  gap: var(--nxt-space-sm);
  overflow-x: auto;
  padding-bottom: var(--nxt-space-xs);
  position: relative;
}

.nx-board__column {
  flex: 0 0 var(--board-column-width);
  display: flex;
  flex-direction: column;
  min-width: 0;
  max-height: 100%;
  border-radius: var(--nxt-radius-md);
  background: var(--nxt-surface-sunken-bg);
  border: var(--nxt-hairline) solid var(--nxt-border-subtle);
  transition:
    border-color var(--nxt-motion-fast) var(--nxt-ease-out),
    background-color var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-board__column.is-disabled {
  opacity: 0.6;
}
.nx-board__column.is-drop-ok {
  border-color: var(--nxt-brand-bg);
  background: var(--nxt-brand-subtle-bg);
}
.nx-board__column.is-drop-rejected {
  border-color: var(--nxt-danger-border);
  background: var(--nxt-danger-subtle-bg);
  cursor: no-drop;
}

.nx-board__header {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  padding: var(--nxt-space-xs) var(--nxt-space-sm);
  min-height: var(--nxt-control-size-md);
}
.nx-board__marker {
  width: calc(var(--nxt-size-unit) * 0.25);
  align-self: stretch;
  border-radius: var(--nxt-radius-full);
  background: var(--board-tone);
}
.nx-board__column:not(.has-tone) .nx-board__marker {
  display: none;
}
/* Today in a week view (`current`). */
.nx-board__column.is-current {
  border-color: var(--nxt-brand-border);
}
.nx-board__column.is-current .nx-board__title {
  color: var(--nxt-brand-bg);
}
.nx-board__title {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-semibold);
  color: var(--nxt-fg);
}
.nx-board__count {
  font-size: var(--nxt-font-size-xs);
  font-variant-numeric: tabular-nums;
  color: var(--nxt-fg-muted);
  padding: 0 var(--nxt-space-xs);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-surface-bg);
  border: var(--nxt-hairline) solid var(--nxt-border-subtle);
}
.nx-board__count.is-over {
  color: var(--nxt-warning-subtle-fg);
  background: var(--nxt-warning-subtle-bg);
  border-color: var(--nxt-warning-border);
}

.nx-board__body {
  list-style: none;
  margin: 0;
  padding: var(--nxt-space-2xs) var(--nxt-space-xs) var(--nxt-space-sm);
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-xs);
  overflow-y: auto;
  min-height: var(--nxt-control-size-xl);
}

.nx-board__card {
  position: relative;
  padding: var(--nxt-space-xs) var(--nxt-space-sm);
  border-radius: var(--nxt-radius-sm);
  background: var(--nxt-surface-raised-bg);
  border: var(--nxt-hairline) solid var(--nxt-border);
  box-shadow: var(--nxt-shadow-sm);
  color: var(--nxt-fg);
  font-size: var(--nxt-font-size-sm);
  cursor: grab;
  outline: none;
}
.nx-board__card.is-static {
  cursor: pointer;
}
.nx-board__card.has-accent {
  border-left: calc(var(--nxt-hairline) * 3) solid var(--board-card-accent);
}
.nx-board__card.is-selected {
  border-color: var(--nxt-brand-bg);
  background: var(--nxt-brand-subtle-bg);
}
.nx-board__card.is-selected.has-accent {
  border-left-color: var(--board-card-accent);
}
.nx-board__card:hover {
  border-color: var(--nxt-border-strong);
}
.nx-board__card:focus-visible {
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
}
.nx-board__card.is-dragging {
  opacity: 0.4;
}
/* Insertion line in the gap the card would land in. */
.nx-board__card.is-drop-before::before,
.nx-board__card.is-drop-after::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  height: calc(var(--nxt-hairline) * 2);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-brand-bg);
  pointer-events: none;
}
.nx-board__card.is-drop-before::before {
  top: calc(var(--nxt-space-xs) / -2 - var(--nxt-hairline));
}
.nx-board__card.is-drop-after::after {
  bottom: calc(var(--nxt-space-xs) / -2 - var(--nxt-hairline));
}

.nx-board__empty {
  padding: var(--nxt-space-sm);
  text-align: center;
  font-size: var(--nxt-font-size-xs);
  color: var(--nxt-fg-subtle);
  border: var(--nxt-hairline) dashed var(--nxt-border);
  border-radius: var(--nxt-radius-sm);
}

.nx-board__footer {
  padding: 0 var(--nxt-space-xs) var(--nxt-space-xs);
}

/* fill (K7): the columns share the width equally and reach the board's full
   height; the board fills its parent (a flex column or a box with a height).
   A column scrolls as a whole — its head stays on top, the footer (a „load
   more“ row) follows the cards inside the scroll, so it comes into view at the
   end of the list. */
.nx-board.is-fill {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(0, 1fr);
  align-items: stretch;
  /* Grows into a parent with a height, takes its content height in one without
     (a basis of 0 would collapse it there). */
  flex: 1 1 auto;
  min-height: 0;
}
.nx-board.is-fill > .nx-board__column {
  max-height: none;
  min-height: 0;
  overflow-y: auto;
}
.nx-board.is-fill > .nx-board__column > .nx-board__header {
  position: sticky;
  top: 0;
  z-index: 1;
  background: inherit;
}
.nx-board.is-fill > .nx-board__column > .nx-board__body {
  flex: 1 0 auto;
  overflow-y: visible;
}

.nx-board__live,
.nx-board__sr {
  position: absolute;
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
  margin: calc(var(--nxt-hairline) * -1);
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}
</style>
